// WYSIWYG conversation editor for the JobModal Conversation tab.
// Tiptap (ProseMirror under the hood) with a constrained extension
// set matching the prototype toolbar: B / I / U / bullet list /
// ordered list / link / @ insert. Output is HTML; the message body
// stores HTML directly and renders via dangerouslySetInnerHTML.
//
// XSS posture: the editor schema only allows the configured nodes
// and marks, so the HTML produced here is well-formed and tag-
// constrained. Server-side validation rejects anything over 8000
// chars; a hardening pass for hostile clients (script tags via
// curl) can come later with DOMPurify on render.
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Mention from "@tiptap/extension-mention";
import { useMemo, useState } from "react";
import { makeMentionSuggestion } from "./conversationMentions";

interface Props {
  initialHtml?: string;
  // mentions is the deduped list of planner_user UUIDs the user
  // picked via @ — the route layer uses them as the authoritative
  // recipient list for the notification dispatch.
  onSubmit: (html: string, mentions: string[]) => Promise<void>;
  authorLabel: string;
}

// UUID v4-ish — matches what the planner_users id column emits.
// Anything not matching gets dropped so a forged data-id from a
// stale render can't pollute the dispatch list.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MENTION_ID_RE = /<span[^>]*\bdata-type="mention"[^>]*\bdata-id="([^"]+)"/g;

function extractMentionIds(html: string): string[] {
  const seen = new Set<string>();
  for (const m of html.matchAll(MENTION_ID_RE)) {
    const id = m[1];
    if (id && UUID_RE.test(id)) seen.add(id);
  }
  return Array.from(seen);
}

export function ConversationEditor({ initialHtml = "", onSubmit, authorLabel }: Props) {
  const [posting, setPosting] = useState(false);
  const mentionExt = useMemo(
    () => Mention.configure({
      HTMLAttributes: { class: "bsp-mention" },
      renderHTML: ({ options, node }) => {
        const label = String(node.attrs.label ?? node.attrs.id ?? "");
        return [
          "span",
          {
            ...options.HTMLAttributes,
            "data-type": "mention",
            "data-id": String(node.attrs.id ?? ""),
            "data-label": label,
          },
          `@${label}`,
        ];
      },
      suggestion: makeMentionSuggestion(),
    }),
    [],
  );
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Placeholder.configure({
        placeholder: "Write a comment… use @ to mention a colleague",
      }),
      mentionExt,
    ],
    content: initialHtml,
    // The editor lives inside a contenteditable; aria-label keeps
    // screen readers grounded since the placeholder isn't a real
    // label.
    editorProps: {
      attributes: {
        "aria-label": "Conversation message",
        style: "min-height: 90px; padding: 10px 12px; outline: none; font-size: 12px; line-height: 1.5;",
      },
    },
  });

  const submit = async () => {
    if (!editor || posting) return;
    if (editor.isEmpty) return;
    setPosting(true);
    try {
      const html = editor.getHTML();
      await onSubmit(html, extractMentionIds(html));
      editor.commands.clearContent();
    } catch (e) {
      window.alert(`Post failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setPosting(false);
    }
  };

  if (!editor) {
    return <div style={{ padding: 14, fontSize: 12, color: "var(--bsp-fg-muted)" }}>Loading editor…</div>;
  }

  return (
    <div style={{
      border: "1px solid var(--bsp-border-strong)",
      borderRadius: 4,
      background: "var(--bsp-surface)",
      display: "flex",
      flexDirection: "column",
    }}>
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 10,
        padding: "8px 10px",
        background: "var(--bsp-surface-muted)",
        borderTop: "1px solid var(--bsp-border)",
        fontSize: 11,
        color: "var(--bsp-fg-muted)",
      }}>
        <span>Posting as <strong style={{ color: "var(--bsp-fg)" }}>{authorLabel}</strong></span>
        <button type="button" className="btn-sm" onClick={() => editor.commands.clearContent()} disabled={posting}>
          Clear
        </button>
        <button
          type="button"
          className="bsp-btn bsp-btn-primary"
          onClick={() => void submit()}
          disabled={posting || editor.isEmpty}
        >
          {posting ? "Posting…" : "Post"}
        </button>
      </div>
    </div>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  const tool = (key: string, label: string, onClick: () => void, active = false, title?: string) => (
    <button
      key={key}
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title ?? label}
      style={{
        background: active ? "var(--bsp-blue-pale)" : "transparent",
        border: "none",
        padding: "4px 8px",
        fontSize: 12,
        fontWeight: key === "b" ? 800 : key === "i" ? 400 : 600,
        fontStyle: key === "i" ? "italic" : "normal",
        textDecoration: key === "u" ? "underline" : "none",
        color: active ? "var(--bsp-blue)" : "var(--bsp-fg)",
        cursor: "pointer",
        borderRadius: 3,
        minWidth: 26,
      }}
    >{label}</button>
  );
  const promptLink = () => {
    const prev = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Enter URL", prev ?? "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      padding: "5px 8px",
      borderBottom: "1px solid var(--bsp-border)",
      background: "var(--bsp-surface-muted)",
    }}>
      {tool("b", "B", () => editor.chain().focus().toggleBold().run(), editor.isActive("bold"), "Bold (Ctrl/Cmd-B)")}
      {tool("i", "I", () => editor.chain().focus().toggleItalic().run(), editor.isActive("italic"), "Italic (Ctrl/Cmd-I)")}
      {tool("u", "U", () => editor.chain().focus().toggleUnderline().run(), editor.isActive("underline"), "Underline (Ctrl/Cmd-U)")}
      <Sep />
      {tool("ul", "• List", () => editor.chain().focus().toggleBulletList().run(), editor.isActive("bulletList"), "Bullet list")}
      {tool("ol", "1. List", () => editor.chain().focus().toggleOrderedList().run(), editor.isActive("orderedList"), "Numbered list")}
      <Sep />
      {tool("link", "🔗", promptLink, editor.isActive("link"), "Add or edit link")}
      {tool("at", "@", () => editor.chain().focus().insertContent("@").run(), false, "Type @ to open the mention picker")}
    </div>
  );
}

function Sep() {
  return <span style={{ width: 1, height: 16, background: "var(--bsp-border)", margin: "0 4px" }} />;
}
